import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CheckIcon, ErrorIcon } from './StepperIcons';
import { stepperSizeStyles, statusStyles } from './stepperStyles';
import type { Step, StepStatus, StepperSize } from './stepperTypes';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Props for a single step of the Stepper
 */
interface StepperStepProps {
  /** Step to render */
  step: Step;
  /** Resolved status of this step */
  status: StepStatus;
  /** Zero-based position, also the fallback label inside the circle */
  index: number;
  /** Whether this is the last step (no connector) */
  isLast: boolean;
  /** Vertical orientation */
  isVertical: boolean;
  /** Size variant */
  size: StepperSize;
  /** Whether the connector after this step is drawn as filled */
  connectorFilled: boolean;
}

/**
 * Waehlt den Inhalt des Schrittkreises: eigenes Icon, Haken, Kreuz oder Nummer.
 */
const renderStepIcon = (step: Step, status: StepStatus, index: number): ReactNode => {
  if (step.icon) return step.icon;
  if (status === 'completed') return <CheckIcon />;
  if (status === 'error') return <ErrorIcon />;
  return <span>{index + 1}</span>;
};

/**
 * Einzelner Schritt des Steppers: Kreis, Verbindungslinie und Beschriftung.
 *
 * Bewusst auf Modulebene definiert: Innerhalb von `Stepper` deklariert waere es
 * bei jedem Render ein neuer Komponententyp, wodurch React den Teilbaum jedes
 * Mal neu einhaengt.
 */
export const StepperStep = ({
  step,
  status,
  index,
  isLast,
  isVertical,
  size,
  connectorFilled,
}: StepperStepProps) => {
  const sizes = stepperSizeStyles[size];
  const statusStyle = statusStyles[status];

  return (
    <div
      className={cn(
        'flex',
        isVertical ? 'flex-row' : 'flex-col items-center',
        !isLast && (isVertical ? '' : 'flex-1')
      )}
      role="listitem"
    >
      <div className={cn('flex', isVertical ? 'flex-col items-center' : 'items-center')}>
        {/* Step Circle */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: index * 0.1 }}
          className={cn(
            sizes.circle,
            'flex items-center justify-center',
            'rounded-full',
            'backdrop-blur-md',
            'border-2',
            'transition-colors duration-300',
            statusStyle.circle,
            statusStyle.icon,
            'font-semibold'
          )}
          aria-current={status === 'current' ? 'step' : undefined}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={status}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {renderStepIcon(step, status, index)}
            </motion.span>
          </AnimatePresence>
        </motion.div>

        {/* Connector Line */}
        {!isLast && (
          <div
            className={cn(
              'relative',
              isVertical
                ? cn('h-12 my-1', sizes.connectorVertical)
                : cn('w-full min-w-[3rem] mx-2', sizes.connector),
              'bg-white/[0.08]',
              'rounded-full',
              'overflow-hidden'
            )}
          >
            <motion.div
              className={cn(
                'absolute inset-0',
                isVertical ? 'bottom-auto' : 'right-auto',
                'bg-success/80',
                'rounded-full'
              )}
              initial={isVertical ? { height: 0 } : { width: 0 }}
              animate={
                connectorFilled ? (isVertical ? { height: '100%' } : { width: '100%' }) : {}
              }
              transition={{ duration: 0.3, delay: 0.2 }}
            />
          </div>
        )}
      </div>

      {/* Labels */}
      <div
        className={cn(
          isVertical ? 'ml-4 pb-8' : 'mt-2 text-center',
          isLast && isVertical && 'pb-0'
        )}
      >
        <p
          className={cn(
            sizes.label,
            'font-medium',
            status === 'current' ? 'text-surface-900' : 'text-surface-500'
          )}
        >
          {step.label}
        </p>
        {step.description && (
          <p className={cn(sizes.description, 'text-surface-500 mt-0.5')}>{step.description}</p>
        )}
      </div>
    </div>
  );
};
